import React from 'react';
import _isNil from 'lodash/isNil';
import { StyleProp, View, ViewStyle } from 'react-native';
import Stack from '../Stack';
import Text from '../Text';
import { colors } from '../../../utils/theme';
import { useTableCellDimensions } from '../../../hooks/useTableCellDimensions';
import { useElementPosition } from '../../../hooks/useElementPosition';
import { PRScrollView } from '../../shared/Scroll';

export type ColumnConfig<TData> = {
    header: string;
    getContent: (data: TData) => string | number | null | undefined;
};

export type TableConfig<TData> = {
    columns: ColumnConfig<TData>[];
};

export type TableOwnProps<TData> = {
    data: TData[];
    config: TableConfig<TData>;
    style?: StyleProp<ViewStyle>;
};

export type TableProps<TData> = TableOwnProps<TData>;

const tableRowPadding = 8;

export type GetTableCellLayoutHandlerArgs = {
    colIndex: number;
    rowIndex: number;
    isHeader: boolean;
    contentKey: string;
};

function Table<TData>({ data, config, style }: TableProps<TData>) {
    const { ref, position, onLayout } = useElementPosition({});
    const { getCellStyle, getTableCellLayoutHandler } = useTableCellDimensions({
        tableWidth: position?.width || null,
        columnCount: config.columns.length,
        minWidth: '100%',
    });

    return (
        <PRScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ width: '100%' }}
            contentContainerStyle={{ minWidth: '100%' }}
        >
            <View style={{ width: '100%' }} onLayout={onLayout} ref={ref}>
                <Stack vertical style={style} fullWidth>
                    <Stack
                        horizontal
                        fullWidth
                        style={{
                            borderBottomWidth: 2,
                            borderBottomColor: colors['Dark-Text'],
                        }}
                    >
                        {config.columns.map((column, index) => (
                            <View
                                style={[
                                    { padding: tableRowPadding },
                                    getCellStyle({ colIndex: index, isHeader: true }),
                                ]}
                                key={`${column.header}-${index}`}
                                onLayout={getTableCellLayoutHandler({
                                    isHeader: true,
                                    colIndex: index,
                                    contentKey: column.header,
                                })}
                            >
                                <Text variant="table-header">{column.header}</Text>
                            </View>
                        ))}
                    </Stack>
                    {data.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                            <Stack
                                horizontal
                                fullWidth
                                style={{
                                    backgroundColor: rowIndex % 2 === 0 ? colors['White'] : colors['Background'],
                                }}
                            >
                                {config.columns.map((column, colIndex) => (
                                    <View
                                        style={[{ padding: tableRowPadding }, getCellStyle({ colIndex, rowIndex })]}
                                        onLayout={getTableCellLayoutHandler({
                                            colIndex,
                                            rowIndex,
                                            contentKey: String(column.getContent(row)),
                                        })}
                                        key={`${column.header}-${colIndex}`}
                                        testID="table-cell"
                                    >
                                        <Text key={`${column.header}-${colIndex}`} variant="body-2">
                                            {column.getContent(row)}
                                        </Text>
                                    </View>
                                ))}
                            </Stack>
                        </React.Fragment>
                    ))}
                </Stack>
            </View>
        </PRScrollView>
    );
}

export default Table;
